import * as React from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { ReasonDialog } from "@/components/ReasonDialog"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/components/ui/use-toast"
import { t } from "@/lib/i18n"
import { onWriteError } from "@/lib/writeErrors"

import { guardarCadencia, type Cadencia } from "@/routes/cobranza/api"

/**
 * CUÁNDO AVISO: la cadencia de cobranza, editable donde se usa.
 *
 * Cesar, 2026-10-02: «Políticas de cobranza, ¿en dónde se colocan ahora?».
 * Había una pestaña en Maestros que guardaba la cadencia con unas claves y el
 * motor leía OTRAS, así que editarla no cambiaba ningún aviso. Se quitó. Esto
 * es el formulario que sí cambia los avisos: escribe exactamente las claves que
 * `cobranza/cadencia.py` lee, y vive al lado de la cartera, que es donde se
 * nota que una factura pide aviso hoy.
 *
 * Es la política del CLIENTE ENTERO (todas sus sociedades). Guardar cierra la
 * vigente y abre una nueva —el historial de por qué se reclamó cada factura
 * cuando se reclamó no se reescribe—, y por eso pide motivo.
 */

const DIAS = [
  { n: 1, label: "L" }, { n: 2, label: "M" }, { n: 3, label: "X" }, { n: 4, label: "J" },
  { n: 5, label: "V" }, { n: 6, label: "S" }, { n: 7, label: "D" },
]

/** «7, 1» → [7, 1]. Tolera espacios, punto y coma y duplicados. */
function listaDeDias(texto: string): number[] {
  const vistos = new Set<number>()
  for (const trozo of texto.split(/[,;\s]+/)) {
    const n = Number(trozo)
    if (trozo && Number.isInteger(n) && n >= 0) vistos.add(n)
  }
  return [...vistos].sort((a, b) => b - a)
}

export function CadenciaDialog({
  open,
  onOpenChange,
  vigente,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  /** La política efectiva que devuelve `/cobranza/deudores`. */
  vigente: Record<string, unknown> | undefined
}) {
  const qc = useQueryClient()
  const { toast } = useToast()

  const [diasCredito, setDiasCredito] = React.useState("30")
  const [avisosPrevios, setAvisosPrevios] = React.useState("7, 1")
  const [avisoElDia, setAvisoElDia] = React.useState(true)
  const [seguimiento, setSeguimiento] = React.useState<number[]>([2, 4])
  const [escalar, setEscalar] = React.useState("21")
  const [incobrable, setIncobrable] = React.useState("180")

  // Al abrir, se parte de lo que está vigente y no de lo de fábrica: editar es
  // ajustar lo que hay, no volver a empezar.
  React.useEffect(() => {
    if (!open) return
    const v = vigente ?? {}
    setDiasCredito(String(v.dias_credito ?? 30))
    setAvisosPrevios(((v.avisos_previos as number[] | undefined) ?? [7, 1]).join(", "))
    setAvisoElDia(v.aviso_el_dia !== false)
    setSeguimiento((v.dias_de_seguimiento as number[] | undefined) ?? [2, 4])
    setEscalar(String(v.escalar_a_los_dias ?? 21))
    setIncobrable(String(v.alerta_incobrable_a_los_dias ?? 180))
  }, [open, vigente])

  const cadencia: Cadencia = {
    dias_credito: Number(diasCredito) || 0,
    avisos_previos: listaDeDias(avisosPrevios),
    aviso_el_dia: avisoElDia,
    dias_de_seguimiento: [...seguimiento].sort(),
    escalar_a_los_dias: Number(escalar) || 0,
    alerta_incobrable_a_los_dias: Number(incobrable) || 0,
  }
  const invalida = cadencia.escalar_a_los_dias <= 0 || cadencia.alerta_incobrable_a_los_dias <= 0
    || cadencia.alerta_incobrable_a_los_dias < cadencia.escalar_a_los_dias

  const guardar = useMutation({
    mutationFn: (motivo: string) => guardarCadencia(cadencia, motivo),
    onSuccess: () => {
      toast({ title: "Cadencia guardada", description: "Desde hoy los avisos siguen esta regla. Lo ya enviado no cambia." })
      void qc.invalidateQueries({ queryKey: ["cobranza-deudores"] })
      onOpenChange(false)
    },
    onError: onWriteError("la cadencia de cobranza"),
  })

  const alternarDia = (n: number) =>
    setSeguimiento((prev) => (prev.includes(n) ? prev.filter((d) => d !== n) : [...prev, n]))

  return (
    <ReasonDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("Cuándo aviso")}
      description={t("Para todas tus sociedades. Guardar cierra la regla vigente y abre ésta desde hoy: lo ya enviado se explica con la anterior.")}
      confirmLabel={t("Guardar la cadencia")}
      pending={guardar.isPending}
      suggestions={["Ajuste de la cadencia por acuerdo con gerencia.", "Los clientes grandes pagan a 60 días."]}
      onConfirm={(motivo) => {
        if (invalida) {
          toast({ variant: "destructive", title: "Revisa los días", description: "Escalar y dar por incobrable tienen que ser días positivos, y el incobrable no puede ir antes que el escalamiento." })
          return
        }
        return guardar.mutateAsync(motivo).then(() => undefined)
      }}
      summary={
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="cad-credito">{t("Vence a los (días)")}</Label>
              <Input id="cad-credito" type="number" min={0} value={diasCredito}
                     onChange={(e) => setDiasCredito(e.target.value)} className="num" />
              <p className="text-2xs text-muted-foreground">{t("Desde la emisión. Si el cliente tiene su plazo, manda el suyo.")}</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="cad-previos">{t("Avisos antes de vencer (días)")}</Label>
              <Input id="cad-previos" value={avisosPrevios}
                     onChange={(e) => setAvisosPrevios(e.target.value)} placeholder="7, 1" />
              <p className="text-2xs text-muted-foreground">{t("Separados por coma. «7, 1» es una semana antes y la víspera.")}</p>
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs">
            <Checkbox checked={avisoElDia} onCheckedChange={(v) => setAvisoElDia(v === true)} />
            {t("Aviso el día del vencimiento")}
          </label>

          <div className="space-y-1">
            <Label>{t("Después de vencida, escribo los")}</Label>
            <div className="flex gap-1">
              {DIAS.map((d) => {
                const activo = seguimiento.includes(d.n)
                return (
                  <button
                    key={d.n}
                    type="button"
                    aria-pressed={activo}
                    onClick={() => alternarDia(d.n)}
                    className={
                      "h-7 w-8 rounded-md border text-xs font-medium transition-colors " +
                      (activo ? "border-brand bg-brand-soft text-foreground" : "text-muted-foreground hover:bg-row-hover")
                    }
                  >
                    {d.label}
                  </button>
                )
              })}
            </div>
            <p className="text-2xs text-muted-foreground">{t("Martes y jueves es lo que trae de fábrica. Sin ningún día marcado no hay seguimiento.")}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="cad-escalar">{t("Escalo al gerente a los (días vencida)")}</Label>
              <Input id="cad-escalar" type="number" min={1} value={escalar}
                     onChange={(e) => setEscalar(e.target.value)} className="num" />
              <p className="text-2xs text-muted-foreground">{t("Dejo de escribir y alguien llama.")}</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="cad-incobrable">{t("Aviso de probable incobrable a los (días)")}</Label>
              <Input id="cad-incobrable" type="number" min={1} value={incobrable}
                     onChange={(e) => setIncobrable(e.target.value)} className="num" />
              <p className="text-2xs text-muted-foreground">{t("Sólo avisa: incobrable lo declara una persona.")}</p>
            </div>
          </div>
        </div>
      }
    />
  )
}
